/**
 * La lecture de l'écran unique des factures.
 *
 * ## Une lecture, et pas deux
 *
 * Les deux écrans fusionnés faisaient la même chose, et la faisaient mal :
 * pour chercher « Dupont », ils lisaient d'abord `users`, puis passaient les
 * uuid trouvés dans un `user_id.in.(...)`. Deux allers-retours, une URL qui
 * grossit avec le nombre d'homonymes, et **aucun tri possible par famille**.
 *
 * `v_admin_factures` porte le nom, le courriel et un champ `recherche` qui
 * concatène tout ce qui identifie une facture. Un `ilike` suffit donc, sur une
 * seule table, et le tri par famille devient une colonne comme une autre.
 *
 * ## La pagination n'est pas une commodité
 *
 * PostgREST plafonne une réponse à **1 000 lignes**, et il ne le dit pas :
 * une lecture non paginée sur 3 711 factures rend une réponse courte, pas une
 * erreur. Toutes les lectures d'ici sont donc bornées par `range()`, et
 * `count: 'exact'` rend le total réel — celui que l'écran doit annoncer, pas
 * celui qu'il affiche.
 *
 * ## Ce qui n'est PAS lu
 *
 * - `invoices.status` : la vue ne l'expose pas (57 % de lignes fausses).
 * - `bank_transactions` : gelée depuis le 27 juillet 2025 (décision D3). Le
 *   détail des paiements passe par `v_reconciliation_journal`, qui lit le
 *   nouveau circuit. Vérifié en production : filtré sur `piece_numero`, le
 *   journal reconstitue `allocated_cents` **au centime, sur les 2 468
 *   factures qui ont reçu de l'argent**.
 */

import { db } from '../payments/db';
import { lireRecherche } from './recherche';
import type {
  Filtre, LigneFacture, LigneProvision,
  Mouvement, NoteDeCredit, Settlement, Tri,
} from './types';
import { TRIS, reclamation } from './types';

export interface CriteresDeFactures {
  recherche: string;
  filtre: Filtre;
  tri: Tri;
  taille: number;
  depuis: number;
}

export interface PageDeFactures {
  lignes: LigneFacture[];
  /** Le nombre réel de factures qui répondent au filtre, pas celui affiché. */
  total: number | null;
  /**
   * Ce que la page affichée totalise en RESTE DÛ, pour que l'écran ne fasse
   * pas croire à un total général.
   *
   * Seules les factures qu'une famille doit encore y entrent. Le solde
   * résiduel d'une facture annulée en est exclu : il est inscrit, mais il
   * n'est réclamé à personne, et l'additionner gonflait le total d'une somme
   * que nul ne doit.
   */
  centimesAffiches: number;
}

/** 25 par page : assez pour balayer, pas assez pour que la page rame. */
export const TAILLE_DE_PAGE = 25;

const CHAMPS = `
  invoice_id, user_id, invoice_number, created_at, due_date, settlement,
  amount_cents, credit_notes_cents, credit_note_count, net_amount_cents,
  allocated_cents, outstanding_cents, overpaid_cents, last_payment_date,
  pdf_url, communication, structured_communication, invoice_email_sent_at,
  document_delivre, nom, prenom, email
`;

interface LigneBrute {
  invoice_id: string;
  user_id: string | null;
  invoice_number: string;
  created_at: string;
  due_date: string | null;
  settlement: string;
  amount_cents: number;
  credit_notes_cents: number;
  credit_note_count: number;
  net_amount_cents: number;
  allocated_cents: number;
  outstanding_cents: number;
  overpaid_cents: number;
  last_payment_date: string | null;
  pdf_url: string | null;
  communication: string | null;
  structured_communication: string | null;
  invoice_email_sent_at: string | null;
  document_delivre: boolean;
  nom: string | null;
  prenom: string | null;
  email: string | null;
}

function versLigne(l: LigneBrute): LigneFacture {
  return {
    invoiceId: l.invoice_id,
    userId: l.user_id,
    invoiceNumber: l.invoice_number,
    creeLe: l.created_at,
    echeanceLe: l.due_date,
    settlement: l.settlement as Settlement,
    factureCents: Number(l.amount_cents),
    notesCents: Number(l.credit_notes_cents),
    notesCount: Number(l.credit_note_count),
    netCents: Number(l.net_amount_cents),
    recuCents: Number(l.allocated_cents),
    resteCents: Number(l.outstanding_cents),
    tropPercuCents: Number(l.overpaid_cents),
    dernierPaiement: l.last_payment_date,
    pdfUrl: l.pdf_url,
    communication: l.communication,
    communicationStructuree: l.structured_communication,
    courrielEnvoyeLe: l.invoice_email_sent_at,
    documentDelivre: Boolean(l.document_delivre),
    nom: l.nom,
    prenom: l.prenom,
    courriel: l.email,
  };
}

/**
 * Une page de factures.
 *
 * Le tri porte un **départage stable** sur `invoice_id`. Sans lui, deux
 * factures du même jour peuvent s'échanger d'une page à l'autre : une ligne
 * serait alors vue deux fois, ou jamais. Le journal de rapprochement a payé ce
 * défaut avant nous.
 */
export async function chargerFactures(criteres: CriteresDeFactures): Promise<PageDeFactures> {
  const ordre = TRIS[criteres.tri] ?? TRIS.recentes;
  const recherche = lireRecherche(criteres.recherche);

  let q = db.from('v_admin_factures').select(CHAMPS, { count: 'exact' });

  q = appliquerFiltre(q, criteres.filtre);

  if (recherche.forme === 'structuree') {
    /*
      Égalité, pas `ilike` : douze chiffres validés par la banque du parent
      désignent UNE facture. Chercher « contient » ici n'élargirait rien
      d'utile et masquerait une communication mal recopiée derrière un
      résultat partiel.
    */
    q = q.eq('structured_communication', recherche.valeur);
  } else if (recherche.forme === 'numero' || recherche.forme === 'texte') {
    /*
      La saisie complète ET ses suites de chiffres. C'est ce qui rend
      équivalents `CDV-NDSC-2601466`, `NDSC-2601466` et `2601466` : le
      deuxième ne diffère du premier que par le préfixe absent, et c'est un
      cas réel.
    */
    const motifs = new Set([recherche.valeur.toLowerCase()]);
    for (const chiffres of recherche.valeur.match(/\d{4,}/g) ?? []) motifs.add(chiffres);
    // Le joker de PostgREST est `*`, converti en `%` côté SQL. Écrire `%`
    // directement ne marche que par accident, selon l'encodage de l'URL.
    q = q.or([...motifs].map((m) => `recherche.ilike.*${m}*`).join(','));
  }

  const { data, error, count } = await q
    .order(ordre.colonne, { ascending: ordre.ascendant, nullsFirst: false })
    .order('invoice_id', { ascending: true })
    .range(criteres.depuis, criteres.depuis + criteres.taille - 1);
  if (error) throw error;

  const lignes = ((data ?? []) as unknown as LigneBrute[]).map(versLigne);
  return {
    lignes,
    total: count ?? null,
    centimesAffiches: lignes.reduce(
      (s, l) => (reclamation(l) === 'due' ? s + l.resteCents : s),
      0,
    ),
  };
}

/* Le type de retour de PostgREST n'est pas exporté par le client ; `any` est
   contenu à cette seule signature plutôt que répandu dans l'appelant. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function appliquerFiltre(q: any, filtre: Filtre): any {
  switch (filtre) {
    case 'tous':
      return q;
    /*
      « Réclame encore un paiement » traverse les états — `unpaid` et
      `partial` — mais EXCLUT les annulées. C'est la correction du
      10 septembre 2026 : le filtre en comptait onze de trop.

      Une facture annulée ne réclame rien à personne. La décision d'annuler
      l'a éteinte pour la famille, même quand son solde reste inscrit faute
      de note de crédit. Les mélanger produisait une liste qui, lue comme
      « qui doit quoi », envoyait relancer des familles dont la commande
      était annulée — exactement ce que la décision D2 interdit.
    */
    case 'reclame':
      return q.gt('outstanding_cents', 0).neq('settlement', 'cancelled');
    /*
      Le stock de la règle D1 : annulée, document PARTI, solde encore
      inscrit. Dix en production, 1 093,00 EUR. C'est la seule population qui
      appelle une note de crédit.
    */
    case 'a_crediter':
      return q.eq('settlement', 'cancelled')
        .gt('outstanding_cents', 0)
        .eq('document_delivre', true);
    /*
      Et son inverse, qui garde son filtre plutôt que de disparaître : annulée
      avant qu'aucun document ne sorte. Rien à réclamer, rien à créditer
      (§5.6) — mais le §5.6 interdit aussi de faire disparaître la
      contradiction par un filtre, donc on la nomme au lieu de l'effacer.
    */
    case 'annulee_avant_envoi':
      return q.eq('settlement', 'cancelled')
        .gt('outstanding_cents', 0)
        .eq('document_delivre', false);
    case 'trop_percu':
      return q.gt('overpaid_cents', 0);
    default:
      return q.eq('settlement', filtre);
  }
}

/**
 * Les notes de crédit des factures affichées.
 *
 * Lues pour la page seulement — jamais pour les 3 711 factures. La dualité de
 * `credit_notes.invoice_id` est déjà résolue par `v_invoice_credit_note_list`
 * (migration `20260910002905`) : cette fonction n'a donc pas à connaître le
 * défaut, et c'est le but. Le résoudre dans chaque appelant, c'était le
 * résoudre différemment dans chacun.
 */
export async function chargerNotesDeCredit(
  invoiceIds: string[],
): Promise<Map<string, NoteDeCredit[]>> {
  const parFacture = new Map<string, NoteDeCredit[]>();
  if (invoiceIds.length === 0) return parFacture;

  const { data, error } = await db
    .from('v_invoice_credit_note_list')
    .select('invoice_id, credit_note_id, credit_note_number, amount_cents, created_at, pdf_url, type, reason, notes, status')
    .in('invoice_id', invoiceIds)
    .order('created_at', { ascending: true });
  if (error) throw error;

  for (const l of (data ?? []) as unknown as {
    invoice_id: string; credit_note_id: string; credit_note_number: string;
    amount_cents: number; created_at: string; pdf_url: string | null;
    type: string | null; reason: string | null; notes: string | null; status: string | null;
  }[]) {
    const note: NoteDeCredit = {
      creditNoteId: l.credit_note_id,
      invoiceId: l.invoice_id,
      numero: l.credit_note_number,
      montantCents: Number(l.amount_cents),
      emiseLe: l.created_at,
      pdfUrl: l.pdf_url,
      type: l.type,
      motif: l.reason,
      notes: l.notes,
      statut: l.status,
    };
    const liste = parFacture.get(l.invoice_id);
    if (liste) liste.push(note);
    else parFacture.set(l.invoice_id, [note]);
  }
  return parFacture;
}

/**
 * Les mouvements d'argent d'une facture.
 *
 * Le filtre porte sur `piece_numero`, la pièce que le mouvement cite. C'est
 * une égalité et non une recherche textuelle : une facture est désignée par
 * son numéro, pas approchée.
 *
 * Le plafond de 200 est un garde-fou, pas une limite attendue : la facture la
 * plus fragmentée de la production porte une poignée de mouvements. S'il était
 * atteint un jour, l'écran le dirait plutôt que de tronquer en silence.
 */
export async function chargerMouvements(invoiceNumber: string): Promise<Mouvement[]> {
  const { data, error } = await db
    .from('v_reconciliation_journal')
    .select(`sens, ligne_id, decide_le, date_valeur, montant_cents, mouvement_cents,
             cible, methode, notes, communication, compte_contrepartie, nom_contrepartie`)
    .eq('piece_numero', invoiceNumber)
    .order('date_valeur', { ascending: true })
    .order('ligne_id', { ascending: true })
    .range(0, 199);
  if (error) throw error;

  return ((data ?? []) as unknown as Record<string, unknown>[]).map((l) => ({
    ligneId: String(l.ligne_id),
    sens: l.sens as Mouvement['sens'],
    decideLe: String(l.decide_le),
    dateValeur: String(l.date_valeur),
    montantCents: Number(l.montant_cents),
    mouvementCents: Number(l.mouvement_cents),
    cible: String(l.cible),
    methode: String(l.methode),
    communication: (l.communication as string) ?? null,
    nomContrepartie: (l.nom_contrepartie as string) ?? null,
    compteContrepartie: (l.compte_contrepartie as string) ?? null,
    notes: (l.notes as string) ?? null,
  }));
}

/**
 * Les comptes portant une provision.
 *
 * Non paginé, et c'est justifié par la mesure : **17 familles** en portent une
 * au 10 septembre 2026. Le jour où cette file dépassera la centaine, il faudra
 * la paginer — mais l'inventer paginée aujourd'hui reviendrait à écrire du
 * code qu'on ne peut pas éprouver.
 *
 * Les comptes qui portent **les deux** — une provision ET une dette — passent
 * en tête : ce sont ceux sur lesquels il y a un geste à faire.
 */
export async function chargerProvisions(): Promise<LigneProvision[]> {
  const { data, error } = await db
    .from('v_admin_provisions')
    .select(`user_id, nom, prenom, email, provision_cents, mouvement_count,
             dernier_mouvement, dette_cents, factures_ouvertes, a_reaffecter_cents`)
    .order('a_reaffecter_cents', { ascending: false })
    .order('provision_cents', { ascending: false })
    .range(0, 199);
  if (error) throw error;

  return ((data ?? []) as unknown as Record<string, unknown>[]).map((l) => ({
    userId: String(l.user_id),
    nom: (l.nom as string) ?? null,
    prenom: (l.prenom as string) ?? null,
    courriel: (l.email as string) ?? null,
    provisionCents: Number(l.provision_cents),
    mouvementCount: Number(l.mouvement_count),
    dernierMouvement: (l.dernier_mouvement as string) ?? null,
    detteCents: Number(l.dette_cents),
    facturesOuvertes: Number(l.factures_ouvertes),
    aReaffecterCents: Number(l.a_reaffecter_cents),
  }));
}
